// buildCnhPdf.js (JavaScript)
import { PDFDocument } from 'pdf-lib';

const A4 = { width: 595.28, height: 841.89 }; // 72dpi
const MARGIN = 24;

// ✅ 3MB
const MAX_BYTES = 3 * 1024 * 1024; // 3MB

function fitWithin(srcW, srcH, maxW, maxH) {
  const ratio = Math.min(maxW / srcW, maxH / srcH);
  return { w: srcW * ratio, h: srcH * ratio };
}

function drawInRect(page, img, rect) {
  const dims = fitWithin(img.width, img.height, rect.w, rect.h);
  const x = rect.x + (rect.w - dims.w) / 2;
  const y = rect.y + (rect.h - dims.h) / 2;
  page.drawImage(img, { x, y, width: dims.w, height: dims.h });
}

// ---------- Helpers ----------
function isBlobLike(x) {
  return typeof Blob !== 'undefined' && x instanceof Blob;
}
function ensureBlob(value, label) {
  if (!isBlobLike(value)) {
    throw new Error(`O campo "blob" de ${label} não é um Blob/File válido. Recebido: ${Object.prototype.toString.call(value)}`);
  }
  return value;
}
function createCanvas(w, h) {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  return c;
}
async function toImageBitmap(blob) {
  if (typeof createImageBitmap === 'function') return await createImageBitmap(blob);
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((res, rej) => {
      const el = new Image();
      el.onload = () => res(el);
      el.onerror = rej;
      el.src = url;
    });
    const canvas = createCanvas(img.naturalWidth || img.width, img.naturalHeight || img.height);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0);
    return { width: canvas.width, height: canvas.height, _canvas: canvas };
  } finally {
    URL.revokeObjectURL(url);
  }
}
function scaleDims(w, h, scale) {
  return { w: Math.max(1, Math.round(w * scale)), h: Math.max(1, Math.round(h * scale)) };
}
async function recompressToJpeg(blob, quality, scale) {
  const bmp = await toImageBitmap(blob);
  const srcW = bmp.width || bmp._canvas?.width;
  const srcH = bmp.height || bmp._canvas?.height;
  const { w, h } = scaleDims(srcW, srcH, scale);
  const canvas = createCanvas(w, h);
  const ctx = canvas.getContext('2d');

  if (typeof ImageBitmap !== 'undefined' && bmp instanceof ImageBitmap) {
    ctx.drawImage(bmp, 0, 0, w, h);
    if (typeof bmp.close === 'function') try { bmp.close(); } catch {}
  } else {
    ctx.drawImage(bmp._canvas, 0, 0, w, h);
  }

  let out;
  if (typeof canvas.convertToBlob === 'function') {
    out = await canvas.convertToBlob({ type: 'image/jpeg', quality });
  } else {
    out = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
  }
  if (!out) throw new Error('Falha ao gerar blob JPEG na recompressão.');
  return out;
}

// ---------- Montagem do PDF ----------
async function generatePdfBytes({ frontBlob, backBlob }) {
  const pdf = await PDFDocument.create();
  const page = pdf.addPage([A4.width, A4.height]);

  const embedJpgBlob = async (b) => {
    const bytes = new Uint8Array(await b.arrayBuffer());
    return await pdf.embedJpg(bytes);
  };

  if (frontBlob && backBlob) {
    const topRect = { x: MARGIN, y: A4.height / 2 + MARGIN, w: A4.width - MARGIN * 2, h: A4.height / 2 - MARGIN * 2 };
    const bottomRect = { x: MARGIN, y: MARGIN,               w: A4.width - MARGIN * 2, h: A4.height / 2 - MARGIN * 2 };

    const imgFront = await embedJpgBlob(frontBlob);
    const imgBack  = await embedJpgBlob(backBlob);
    drawInRect(page, imgFront, topRect);
    drawInRect(page, imgBack,  bottomRect);
  } else {
    const only = frontBlob || backBlob;
    const img = await embedJpgBlob(only);
    const rect = { x: MARGIN, y: MARGIN, w: A4.width - MARGIN * 2, h: A4.height - MARGIN * 2 };
    drawInRect(page, img, rect);
  }
  return await pdf.save();
}

export async function buildCnhPdf(items, filename = 'documento.pdf') {
  if (!items || !items.length) throw new Error('Nenhuma imagem recebida.');

  const documento = (items[0] && items[0].documento) ? items[0].documento : 'document';

  const valid = items.filter(it => it && isBlobLike(it.blob));
  if (!valid.length) throw new Error('Nenhum item possui um Blob/File válido em "blob".');

  let topItem = null;
  let bottomItem = null;

  if (valid.length >= 2) {
    const frontItem = valid.find(i => i.side === 'front');
    const backItem  = valid.find(i => i.side === 'back');
    if (frontItem || backItem) {
      topItem = frontItem || valid[0];
      bottomItem = backItem  || (topItem === valid[0] ? valid[1] : valid[0]);
    } else {
      topItem = valid[0];
      bottomItem = valid[1];
    }
  } else {
    topItem = valid[0];
    bottomItem = null;
  }

  let topSrc = ensureBlob(topItem.blob, 'top');
  let bottomSrc = bottomItem ? ensureBlob(bottomItem.blob, 'bottom') : null;

  // ✅ “diminui um pouco a qualidade” (mais agressivo desde o início)
  let quality = 0.8;   // antes: 0.9
  let scale = 1.0;

  const MIN_QUALITY = 0.35; // antes: 0.4 (deixa ir um pouco mais se precisar)
  const MIN_SCALE   = 0.5;

  let bestBytes = null;

  async function attempt() {
    const topCompressed = await recompressToJpeg(topSrc, quality, scale);
    const bottomCompressed = bottomSrc ? await recompressToJpeg(bottomSrc, quality, scale) : null;

    return await generatePdfBytes({
      frontBlob: topCompressed,
      backBlob: bottomCompressed || null
    });
  }

  for (let tries = 0; tries < 12; tries++) {
    const bytes = await attempt();
    bestBytes = bytes;
    if (bytes.byteLength <= MAX_BYTES) break;

    // ✅ reduz quality um pouco mais por tentativa
    if (quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, +(quality - 0.12).toFixed(2)); // antes: -0.1
    } else if (scale > MIN_SCALE) {
      scale = Math.max(MIN_SCALE, +(scale * 0.9).toFixed(2));
    } else {
      break;
    }
  }

  const pdfBlob = new Blob([bestBytes], { type: 'application/pdf' });
  return { blob: pdfBlob, documento, tipo: 'document', side: 'both' };
}
